import mqtt from 'mqtt'
import config from '@/config'
import TopicManager from './TopicManager'
import SystemConnection, { CONNECTION_STATUS } from '@/utils/system/SystemConnection'
import { isFeatureEnabled } from '@ahaslides-product/frontend-toolkit'

// Idle keepalive (seconds). At 60s a dead broker connection takes up to ~1.5×
// keepalive to be noticed, which is fine when nobody is presenting.
const KEEPALIVE_DEFAULT = 60
// While presenting we want the connection-status banner (AHA-40903) to surface
// an MQTT drop fast, so the client pings — and therefore detects a missing
// PINGRESP — every 8s instead of every 60s.
const KEEPALIVE_PRESENTING = 8

// When a backgrounded tab is throttled the socket can become a "zombie": TCP
// still looks open locally but the broker has already dropped us, and no
// close/offline event fires. On tab re-focus we actively probe the path with a
// QoS-1 publish; if the PUBACK doesn't round-trip within this window we treat
// EMQX as disconnected so the banner can show. (AHA-40903)
const WAKEUP_TOPIC = 'wakeup'
const ZOMBIE_ACK_TIMEOUT_MS = 2000

const connectOptions = {
  protocolVersion: 5,
  keepalive: KEEPALIVE_DEFAULT,
}
class MqttClient {
  constructor(url, options) {
    this.client = mqtt.connect(url, options)
    // Bound once so add/removeEventListener share the same reference.
    this._onVisibilityChange = this._handleVisibilityChange.bind(this)
    this.listenConnectionEvents()
  }

  getClient() {
    return this.client
  }

  // Adapt the keepalive to the presenting state. Applied to the live ping timer
  // without a reconnect so existing subscriptions stay intact: mqtt.js rebuilds
  // the timer from `options.keepalive` on every (re)connect, and `reschedule`
  // re-periods the already-running timer. If we are not connected yet the timer
  // is null — setting `options.keepalive` is enough, the next connect picks it up.
  setPresenting(isPresenting) {
    // TEMPORARILY DISABLED: the on-focus zombie probe (_handleVisibilityChange) is
    // suspected of leaving the "Connecting to server..." banner stuck when switching
    // presenting mode. Disabled pending root-cause investigation; the presenting
    // keepalive (8s) below still surfaces real broker drops. Re-enable once fixed.
    // this._setVisibilityHandler(isPresenting)
    const seconds = isPresenting ? KEEPALIVE_PRESENTING : KEEPALIVE_DEFAULT
    if (!this.client || this.client.options.keepalive === seconds) {
      return
    }
    this.client.options.keepalive = seconds
    if (this.client.pingTimer && typeof this.client.pingTimer.reschedule === 'function') {
      this.client.pingTimer.reschedule(seconds * 1000)
    }
  }

  // Attach/detach the tab-visibility probe. addEventListener with the same bound
  // reference is idempotent, so repeated true/false calls won't stack listeners.
  _setVisibilityHandler(isPresenting) {
    if (typeof document === 'undefined') {
      return
    }
    if (isPresenting) {
      document.addEventListener('visibilitychange', this._onVisibilityChange)
    } else {
      document.removeEventListener('visibilitychange', this._onVisibilityChange)
    }
  }

  // On tab re-focus, detect a zombie connection. If we already know we're
  // disconnected, kick a reconnect; otherwise round-trip a QoS-1 publish and
  // require its PUBACK within ZOMBIE_ACK_TIMEOUT_MS — no ACK means the path is
  // dead even though the socket still looks open.
  _handleVisibilityChange() {
    if (document.visibilityState !== 'visible') {
      return
    }
    const { client } = this
    if (!client.connected && !client.reconnecting) {
      client.reconnect()
    } else if (client.connected) {
      const zombieTimeout = setTimeout(() => {
        SystemConnection.detectEmqxConnection(CONNECTION_STATUS.ERROR)
      }, ZOMBIE_ACK_TIMEOUT_MS)
      client.publish(WAKEUP_TOPIC, JSON.stringify({}), { qos: 1 }, (error) => {
        clearTimeout(zombieTimeout)
        if (error) {
          SystemConnection.detectEmqxConnection(CONNECTION_STATUS.ERROR)
        } else {
          SystemConnection.detectEmqxConnection(CONNECTION_STATUS.GOOD)
        }
      })
    }
  }

  listenConnectionEvents() {
    this.client.on('connect', () => {
      console.log('Connected to MQTT broker')
      SystemConnection.detectEmqxConnection(CONNECTION_STATUS.GOOD)
    })

    this.client.on('reconnect', () => {
      TopicManager.getSubscribedTopics().forEach((topic) => {
        this.client.subscribe(topic)
      })
    })

    this.client.on('close', () => {
      SystemConnection.detectEmqxConnection(CONNECTION_STATUS.ERROR)
    })

    this.client.on('error', () => {
      SystemConnection.detectEmqxConnection(CONNECTION_STATUS.ERROR)
    })

    this.client.on('offline', () => {
      SystemConnection.detectEmqxConnection(CONNECTION_STATUS.ERROR)
    })
  }

  subscribe(topic) {
    this.client.subscribe(topic)
    TopicManager.addTopic(topic)
  }

  unsubscribe(topic) {
    this.client.unsubscribe(topic)
    TopicManager.removeTopic(topic)
  }
}

const mqttClient = new MqttClient(config.mqttBrokerUrl, connectOptions)
const mqttClientInstance = mqttClient.getClient()

const getMessageHandlerWrapper = (handler) => {
  return (topic, message, packet) => {
    if (!packet.retain || isFeatureEnabled('release-mqtt-retain-message')) {
      handler(topic, message, packet)
    }
  }
}

// Use Proxy to intercept subscribe/unsubscribe calls and route them to MqttClient instance
// while forwarding all other calls to the underlying mqtt client
const proxiedClient = new Proxy(mqttClientInstance, {
  get(target, prop) {
    // Intercept subscribe and unsubscribe to use MqttClient methods
    if (prop === 'subscribe') {
      return (...args) => mqttClient.subscribe(...args)
    }
    if (prop === 'unsubscribe') {
      return (...args) => mqttClient.unsubscribe(...args)
    }
    // setPresenting lives on the MqttClient wrapper, not the raw mqtt instance,
    // so route it explicitly like subscribe/unsubscribe.
    if (prop === 'setPresenting') {
      return (...args) => mqttClient.setPresenting(...args)
    }
    if (prop === 'on') {
      return (...args) => {
        const [eventName, ...otherArgs] = args
        if (eventName === 'message') {
          const [handler] = otherArgs
          return target[prop](eventName, getMessageHandlerWrapper(handler))
        }

        return target[prop](...args)
      }
    }
    // Forward all other properties/methods to the underlying mqtt client
    return target[prop]
  },
})

export default proxiedClient
